import { useRef } from "react";
import { MAX_PAYMENT_PROOFS, MAX_PROOF_BYTES } from "../../utils/paymentProofUrls";

/**
 * Multi-file payment proof picker. Requires at least one image; up to MAX_PAYMENT_PROOFS.
 */
export default function PaymentProofAttachments({
  items = [],
  onChange,
  max = MAX_PAYMENT_PROOFS,
  error = null,
  label = "Payment proof attachments *",
  hint = "Upload 1–8 screenshots or photos of the payment (required).",
}) {
  const inputRef = useRef(null);

  const revoke = (preview) => {
    if (preview) URL.revokeObjectURL(preview);
  };

  const setItems = (next) => {
    onChange?.(next);
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const existingKeys = new Set(items.map((it) => `${it.file?.name}:${it.file?.size}`));
    const next = [...items];
    for (const file of incoming) {
      if (next.length >= max) break;
      if (!file.type.startsWith("image/")) continue;
      if (file.size > MAX_PROOF_BYTES) continue;
      const key = `${file.name}:${file.size}`;
      if (existingKeys.has(key)) continue;
      existingKeys.add(key);
      next.push({
        id: `${key}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        preview: URL.createObjectURL(file),
      });
    }
    setItems(next);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (id) => {
    const found = items.find((it) => it.id === id);
    if (found) revoke(found.preview);
    setItems(items.filter((it) => it.id !== id));
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => addFiles(e.target.files)}
        className="w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg bg-white text-gray-700 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-rose-50 file:text-rose-700"
      />
      {items.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((it, idx) => (
            <div
              key={it.id}
              className="relative rounded-lg border border-gray-200 bg-gray-50 overflow-hidden"
            >
              {it.preview ? (
                <img
                  src={it.preview}
                  alt={`Payment proof ${idx + 1}`}
                  className="h-28 w-full object-contain bg-white"
                />
              ) : (
                <div className="h-28 flex items-center justify-center text-xs text-gray-500 px-2 text-center">
                  {it.file?.name || `File ${idx + 1}`}
                </div>
              )}
              <button
                type="button"
                onClick={() => removeAt(it.id)}
                className="absolute top-1 right-1 rounded-full bg-black/70 text-white text-[10px] font-semibold px-2 py-0.5 hover:bg-black"
              >
                Remove
              </button>
              <p className="px-2 py-1 text-[10px] text-gray-500 truncate">{it.file?.name}</p>
            </div>
          ))}
        </div>
      )}
      <p className="mt-1.5 text-xs text-gray-400">
        {items.length}/{max} attached
      </p>
      {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
    </div>
  );
}

export function PaymentProofGallery({ urls = [], empty = "—" }) {
  const list = Array.isArray(urls) ? urls.filter(Boolean) : [];
  if (!list.length) {
    return <span className="text-gray-400 text-xs">{empty}</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {list.map((url, i) => (
        <a
          key={`${url}-${i}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5"
        >
          <img
            src={url}
            alt={`Proof ${i + 1}`}
            className="h-10 w-10 rounded border border-gray-200 object-cover bg-white"
          />
          <span className="text-xs font-semibold text-blue-700 hover:underline">
            {list.length === 1 ? "View" : `#${i + 1}`}
          </span>
        </a>
      ))}
    </div>
  );
}
